/**
 * What a sample-café visitor brings into their own account (owner, 2026-10-05: "Don't lose your progress! Sign in"). The sample
 * runs on cleancleaver.com/demo/ and the app on app.cleancleaver.com, so the progress rides on the sign-up link
 * (lib/demoAsk.ts demoSignupUrl: from=demo&type=…&tried=…&state=…) and is kept here the moment the app loads, like the buy intent:
 * it survives Google's sign-in window, the email code and a reload.
 *
 * TRUE progress only. What is kept: the kind of place they explored, its state, and which features they tried. It presets the new
 * account's setup (shown, still answerable, sent as "to confirm" where it is a guess). With nothing tried there is no progress to
 * keep, and no screen may say there is (`carryLine` is null).
 */
import { WHO_CHOICES, buyState, type UsState } from "@logwave/core";
import type { DemoFeature } from "./demoAsk.ts";

export const CARRY_KEY = "cc.demo.carry";
const FEATURES: readonly DemoFeature[] = ["checks", "cleaning", "binder", "cooling", "report"];
/** How each tried feature is named on the welcome line. */
export const TRIED_WORDS: Record<DemoFeature, string> = {
  checks: "a daily check", cleaning: "a room check", binder: "the inspection binder", cooling: "a cooling timer", report: "a problem report",
};

export interface DemoCarry { typeId?: string; state?: UsState; tried: DemoFeature[]; at: string }

function safeStorage(): Storage | undefined {
  try { return typeof localStorage === "undefined" ? undefined : localStorage; } catch { return undefined; }
}
let memory: DemoCarry | null = null;

/** A sign-up from the sample (?from=demo…): keep what it carried. Returns it, or null for any other visit. */
export function captureDemoCarry(search: string, storage: Storage | undefined = safeStorage(), now = new Date()): DemoCarry | null {
  const p = new URLSearchParams(search);
  if (p.get("from") !== "demo") return null;
  const type = p.get("type") ?? "";
  // The main kinds of place (the same in every state; the full type list is per state, so it isn't read here).
  const typeId = WHO_CHOICES.some((w) => w.typeId && w.typeId === type) ? type : undefined;
  const tried = [...new Set((p.get("tried") ?? "").split(",").filter((f): f is DemoFeature => (FEATURES as readonly string[]).includes(f)))];
  const st = buyState(p.get("state"));
  const v: DemoCarry = { ...(typeId ? { typeId } : {}), ...(st ? { state: st } : {}), tried, at: now.toISOString() };
  memory = v;
  try { storage?.setItem(CARRY_KEY, JSON.stringify(v)); } catch { /* this page load only */ }
  return v;
}

export function demoCarry(storage: Storage | undefined = safeStorage()): DemoCarry | null {
  // Storage first; the in-memory copy only where storage can't be used.
  let v: DemoCarry | null;
  try { v = storage ? JSON.parse(storage.getItem(CARRY_KEY) ?? "null") as DemoCarry | null : memory; } catch { v = memory; }
  return v && Array.isArray(v.tried) ? { ...v, tried: v.tried.filter((f) => (FEATURES as readonly string[]).includes(f)) } : null;
}
export function clearDemoCarry(storage: Storage | undefined = safeStorage()) {
  memory = null;
  try { storage?.removeItem(CARRY_KEY); } catch { /* ignore */ }
}

/** Something real to keep: at least one feature tried. The kind of place alone was the sample's, not their choice. */
export const carryMeaningful = (c: DemoCarry | null | undefined): c is DemoCarry => !!c && c.tried.length > 0;

const NUM = ["no", "one", "two", "three", "four", "five"];
/** "We kept your coffee shop setup and the 3 things you tried." Null when nothing was tried (never a claim of kept progress). */
export function carryLine(c: DemoCarry | null | undefined): string | null {
  if (!carryMeaningful(c)) return null;
  const label = WHO_CHOICES.find((w) => w.typeId && w.typeId === c.typeId)?.label.toLowerCase();
  const n = c.tried.length;
  const things = n === 1 ? "the one thing you tried" : `the ${NUM[n] ?? n} things you tried`;
  return label ? `We kept your ${label} setup and ${things}.` : `We kept ${things}.`;
}

/** "You tried a daily check, a room check and the inspection binder." */
export function triedLine(c: DemoCarry | null | undefined): string | null {
  if (!carryMeaningful(c)) return null;
  const w = c.tried.map((f) => TRIED_WORDS[f]);
  const list = w.length === 1 ? w[0] : `${w.slice(0, -1).join(", ")} and ${w.at(-1)}`;
  return `You tried ${list}. Each one is ready at your place.`;
}
